import {
  getIconText,
  getProgressBarColor,
  getStatusIcon,
} from "components/main-view/utils/functions/progressUtils";
import useMouseClickOutside from "hooks/MouseClickOutside";
import { useEffect, useRef, useState } from "react";
import {
  MdModelTraining,
  MdMoreHoriz,
  MdOutlineCollectionsBookmark,
  MdOutlinePsychology,
  MdOutlineSchool,
} from "react-icons/md";
import { getCollectionRequest } from "services/apiRequests";
import { ContactListType, Course } from "utils/interfaces";
import EditDeleteMenu from "../components/EditDeleteMenu";

interface ClassCardProps {
  classItem: any;
  canEdit: boolean;
  handleCardClick: (event: React.MouseEvent) => void;
  handleDelete: (event: React.MouseEvent) => void;
  setShowTeachersList: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTeachers: React.Dispatch<React.SetStateAction<ContactListType[]>>;
}

const ClassCard: React.FC<ClassCardProps> = ({
  classItem,
  canEdit,
  handleCardClick,
  handleDelete,
  setShowTeachersList,
  setSelectedTeachers,
}) => {
  // States definition
  const [progress, setProgress] = useState(0);
  const [aptitude, setAptitude] = useState(0);
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(true);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const includeEdit = classItem.canEdit || false;
  const includeDelete = classItem.canDelete || false;
  const activateEditButton = includeEdit || includeDelete;

  // Create a menu ref
  const menuRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Definition of the maximum number of courses to show on the card
  const maxCoursesToShow = 3;

  // Definition of the overall value
  const overall = 0.5 * (progress + aptitude);

  /**
   * Handle view all click
   */
  const handleClickViewAll = (event: React.MouseEvent) => {
    // First of all, stop propagating to prevent click on the card
    event.stopPropagation();
    // And open the pop up for showing
    setShowTeachersList(true);
    // Set the selected teachers of this class
    setSelectedTeachers(classItem.teachers);
  };

  /**
   * Function used to get the statistics
   * @returns
   */
  const getClassStatistics = async () => {
    // First of all, set the state
    setIsLoadingStatistics(true);

    // Get the data
    const response = await getCollectionRequest(
      `/api/classes/statistics/${classItem._id}`
    );

    // Stop the animation
    setIsLoadingStatistics(false);

    // Check if the response went right
    if (!response.successful) return;

    // Get the statistics
    const statistics = response.content;

    // Assign to their states
    setProgress(statistics.progressRate);
    setAptitude(statistics.aptitudeRate);
  };

  /**
   * Get the statistics when the card is rendered
   */
  useEffect(() => {
    getClassStatistics();
  }, [classItem._id]);

  /**
   * Hook used when clicking outside the dropdown menu
   */
  useMouseClickOutside(
    menuRef,
    () => setShowOptionsMenu(false),
    showOptionsMenu
  );

  /**
   * Function used to open the pop up menu with the edit and
   * delete option
   * @param event
   */
  const handleClickOptions = (event: React.MouseEvent) => {
    // First of all, prevent clicking the card
    event.stopPropagation();

    // Toggle the state
    setShowOptionsMenu(!showOptionsMenu);
  };

  /**
   * Function used to wrap the edit or delete functions
   * in order to add custom functionalities per card
   */
  const handleClickOptionWrapper = (
    func: (event: React.MouseEvent) => void
  ) => {
    return (event: React.MouseEvent) => {
      // Close the menu when clicking on one of the options
      setShowOptionsMenu(false);

      // And execute the function
      func(event);
    };
  };

  /**
   * Function used to get the teacher name for the lower left side of the card
   * @returns
   */
  // const getTeacherInfo = async () => {
  //   // Check if the class have a teacher
  //   if (!(classItem.teachers && classItem.teachers.length > 0)) return;

  //   // Get the first teacher id
  //   const teacherToShow = classItem.teachers[0];

  //   // Get the content
  //   const response = await getCollectionRequest(
  //     `/api/users/teacher-name?id=${teacherToShow._id}`
  //   );

  //   // Check if the query was successful
  //   if (
  //     response.successful &&
  //     response.content &&
  //     response.content.length > 0
  //   ) {
  //     // Get the info
  //     const teacherInfo = response.content[0];
  //     // Set the teacher name
  //     setTeacherName(`${teacherInfo.firstName} ${teacherInfo.lastName}`);
  //   }
  // };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`w-full h-fit p-6 bg-neutral-25 rounded-xl justify-between items-center inline-flex ${
        canEdit
          ? "cursor-default"
          : "cursor-pointer hover:shadow-card ease-in-out duration-150"
      }`}
      onClick={canEdit ? () => {} : handleCardClick}
    >
      {/* Left section (class name and teacher(s))*/}
      <div className="w-[190px] self-stretch flex-col justify-between items-start inline-flex">
        <div className="self-stretch text-neutral-700 text-lg font-semibold font-sans leading-snug overflow-hidden break-keep whitespace-nowrap text-ellipsis">
          {classItem.name}
        </div>
        <div className="self-stretch justify-center items-center gap-2 inline-flex">
          <MdOutlineSchool className="w-8 h-8 fill-neutral-300" />
          <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
            <div className="text-center text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
              Teacher
            </div>
            <div className="self-stretch text-neutral-600 text-xs font-normal font-sans leading-[14px]">
              {classItem.teachers && classItem.teachers.length > 0
                ? `${classItem.teachers[0].firstName} ${classItem.teachers[0].lastName}`
                : "-"}
            </div>
            {classItem.teachers.length > 1 && (
              <button
                className="w-fit text-neutral-500 text-xs font-normal font-sans underline leading-[14px] cursor-pointer hover:text-neutral-900"
                onClick={handleClickViewAll}
              >
                View all
              </button>
            )}
          </div>
        </div>
      </div>
      {/* <div className="w-[190px] h-full p-1 rounded flex-col justify-start items-start gap-3 inline-flex">
        <div className="self-stretch h-20 flex-col justify-start items-start gap-1 flex">
          <div className="p-2 bg-Canvas rounded-full justify-start items-end gap-2 inline-flex">
            <div className="w-8 h-8 relative">
              <MdOutlineHowToReg className="w-full h-full fill-neutral-700" />
            </div>
          </div>
          <div className="self-stretch justify-start items-end gap-1 inline-flex">
            <div className="w-[41px] text-right text-neutral-700 text-lg font-semibold font-sans leading-snug">
              {classItem.enrollmentRate}%
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 inline-flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-center text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                  Enrolment
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch h-0.5 bg-neutral-100 flex-col justify-start items-start gap-2 flex">
            <div
              className={`h-0.5 ${getProgressBarColor(
                classItem.enrollmentRate
              )}`}
              style={{ width: `${classItem.enrollmentRate}%` }}
            />
          </div>
        </div>
        <div className="self-stretch flex-col justify-start items-start gap-0.5 flex">
          <div className="justify-start items-center gap-1 inline-flex">
            <div className="w-8 text-center text-neutral-500 text-sm font-semibold font-sans leading-[17px]">
              {classItem && classItem.students ? classItem.students.length : 0}
            </div>
            <div className="text-center text-neutral-500 text-xs font-normal uppercase leading-[14px] tracking-wide">
              Students enrolled
            </div>
          </div>
          <div className="justify-start items-center gap-1 inline-flex">
            <div className="w-8 text-center text-neutral-500 text-sm font-semibold font-sans leading-[17px]">
              {classItem.licenses || 0}
            </div>
            <div className="text-center text-neutral-500 text-xs font-normal uppercase leading-[14px] tracking-wide">
              Licenses
            </div>
          </div>
        </div>
      </div> */}
      <div className="w-[190px] h-full p-1 rounded flex-col justify-start items-start gap-1 inline-flex">
        <div className="self-stretch h-[66px] flex-col justify-center items-start gap-1 flex">
          <div className="p-2 bg-neutral-50 rounded-full justify-start items-end gap-2 inline-flex">
            <MdOutlineCollectionsBookmark className="w-8 h-8 fill-neutral-700" />
          </div>
          <div className="self-stretch justify-start items-center gap-2 inline-flex">
            <div className="grow shrink basis-0 text-neutral-500 text-xs font-bold font-sans uppercase leading-[14px] tracking-wide">
              Courses
            </div>
          </div>
        </div>
        <div className="self-stretch h-full flex-col justify-start items-start gap-0.5 flex">
          <div className="self-stretch justify-start items-start flex-col inline-flex overflow-clip">
            {classItem.courses && classItem.courses.length > 0 ? (
              classItem.courses.map((course: Course, index: number) => {
                if (index <= maxCoursesToShow) {
                  return (
                    <div
                      key={index}
                      className="grow shrink basis-0 text-left text-neutral-500 text-xs font-normal font-sans leading-[14px]"
                    >
                      {index === maxCoursesToShow ? "..." : course.title}
                    </div>
                  );
                }
              })
            ) : (
              <div className="grow shrink basis-0 text-left text-neutral-500 text-xs font-normal font-sans leading-[14px]">
                No courses defined yet
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="w-[190px] h-full p-1 rounded flex-col justify-start items-start gap-3 inline-flex">
        <div className="self-stretch h-20 flex-col justify-start items-start gap-1 flex">
          <div className="p-2 bg-Canvas rounded-full justify-start items-end gap-2 inline-flex">
            <MdModelTraining className="w-8 h-8 fill-neutral-700" />
          </div>
          <div className="self-stretch justify-start items-end gap-1 inline-flex">
            <div className="w-fit text-right text-neutral-700 text-lg font-semibold font-sans leading-snug">
              {progress}%
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 inline-flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-center text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                  Progress
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch h-0.5 bg-neutral-100 flex-col justify-start items-start gap-2 flex">
            <div
              className={`h-0.5 ${getProgressBarColor(progress)}`}
              style={{ width: `${progress || 0}%` }}
            />
          </div>
        </div>
        <div className="self-stretch flex-col justify-start items-start gap-0.5 flex">
          <div className="self-stretch h-7 flex-col justify-start items-start gap-0.5 flex">
            <div className="self-stretch text-neutral-500 text-xs font-normal font-sans leading-[14px]">
              Average completion of assigned projects
            </div>
          </div>
        </div>
      </div>
      <div className="w-[190px] h-full p-1 rounded flex-col justify-start items-start gap-3 inline-flex">
        <div className="self-stretch h-20 flex-col justify-start items-start gap-1 flex">
          <div className="p-2 bg-Canvas rounded-full justify-start items-end gap-2 inline-flex">
            <MdOutlinePsychology className="w-8 h-8 fill-neutral-700" />
          </div>
          <div className="self-stretch justify-start items-end gap-1 inline-flex">
            <div className="w-fit text-right text-neutral-700 text-lg font-semibold font-sans leading-snug">
              {aptitude}%
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 inline-flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-center text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                  Aptitude
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch h-0.5 bg-neutral-100 flex-col justify-start items-start gap-2 flex">
            <div
              className={`h-0.5 ${getProgressBarColor(aptitude)}`}
              style={{ width: `${aptitude}%` }}
            />
          </div>
        </div>
        <div className="self-stretch flex-col justify-start items-start gap-0.5 flex">
          <div className="self-stretch h-7 flex-col justify-start items-start gap-0.5 flex">
            <div className="self-stretch text-neutral-500 text-xs font-normal font-sans leading-[14px]">
              Average score on completed projects
            </div>
          </div>
        </div>
      </div>
      <div className="w-[60px] self-stretch justify-between items-center flex">
        <div className="flex-col justify-start items-center gap-1 inline-flex">
          <div className="w-8 h-8 relative">{getStatusIcon(overall)}</div>
          <div className="justify-center items-center gap-2 inline-flex">
            <div className="text-neutral-500 text-xs font-semibold font-sans leading-[14px]">
              {getIconText(overall)}
            </div>
          </div>
        </div>
      </div>
      {canEdit && (
        <div
          className="self-stretch justify-center items-center gap-3 inline-flex relative"
          ref={menuRef}
        >
          <button
            className={`p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex ${
              activateEditButton
                ? "hover:bg-neutral-200 ease-in-out duration-300 cursor-pointer"
                : "cursor-not-allowed"
            }`}
            onClick={activateEditButton ? handleClickOptions : () => {}}
            title={
              activateEditButton
                ? "More options"
                : "This user is not allowed to edit this class"
            }
          >
            <MdMoreHoriz
              className={`w-6 h-6 ${
                activateEditButton ? "fill-neutral-700" : "fill-neutral-200"
              }`}
            />
          </button>
          <EditDeleteMenu
            show={showOptionsMenu}
            includeEdit={includeEdit}
            includeDelete={includeDelete}
            handleEdit={handleClickOptionWrapper(handleCardClick)}
            handleDelete={handleClickOptionWrapper(handleDelete)}
            containerStyle="absolute w-fit top-[92px] right-0 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
          />
        </div>
      )}
    </div>
  );
};

export default ClassCard;
